import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@clerk/react';
import { EmailRequestType, LabelId } from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';
import { isOwnMailboxCommunication } from '../../../utils/userCommunicationHelpers';
import { useActiveInboxEntriesSlice } from '../../InboxEntries';
import { useCustomToast } from '../../useCustomToast';

export const useMarkAsUnreadUserCommunication = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const skipEffectRef = useRef(false);
  const { errorMessage } = useCustomToast();
  const {
    unReadUserCommunication,
    setMarkAsUnreadLoading,
    getUserCommunicationByMessageId,
  } = useUserCommunicationsStore();
  const {
    unreadUserCommunicationsInInboxEntries,
    modifyInboxEntryUserCommunication,
  } = useActiveInboxEntriesSlice();

  const handleMarkAsUnreadUserCommunication = useCallback(
    async (messageIds: string | string[], onSuccess?: () => void) => {
      const messageIdsArray = Array.isArray(messageIds)
        ? messageIds
        : [messageIds];

      if (messageIdsArray.length === 0) {
        return;
      }

      const validCommunications = messageIdsArray.filter((messageId) => {
        const userCommunication = getUserCommunicationByMessageId(messageId);
        if (!userCommunication) {
          log.error(`UserCommunication with messageId ${messageId} not found`);
          return false;
        }
        return isOwnMailboxCommunication(userCommunication, user?.id);
      });

      if (validCommunications.length === 0) {
        errorMessage(t('inbox.userCommunication.actions.markAsUnreadError'));
        return;
      }

      setMarkAsUnreadLoading(true);

      try {
        const operations = validCommunications.map(async (messageId) => {
          const userCommunication = getUserCommunicationByMessageId(messageId);

          if (!userCommunication) {
            return { messageId, success: false };
          }

          try {
            if (!userCommunication.userEmailAddress) {
              throw new Error('No user email address found in communication');
            }

            if (!userCommunication.platform) {
              throw new Error('No platform found in communication');
            }

            await modifyEmail({
              userEmailAddress: userCommunication.userEmailAddress,
              platform: userCommunication.platform,
              messageId: userCommunication.messageId,
              action: EmailRequestType.UNREAD,
            });

            return { messageId, success: true };
          } catch (error) {
            log.error(
              `Failed to mark email as unread for messageId ${messageId}:`,
              error,
            );
            return { messageId, success: false, error };
          }
        });

        const results = await Promise.all(operations);
        const succeeded = results.filter((result) => result.success);
        const failed = results.filter((result) => !result.success);

        if (succeeded.length > 0) {
          const succeededMessageIds = succeeded.map(
            (result) => result.messageId,
          );

          succeededMessageIds.forEach((id) => {
            const comm = getUserCommunicationByMessageId(id);
            if (!comm) {
              return;
            }

            unReadUserCommunication(id);

            comm.inboxEntryIds?.forEach((entryId) => {
              modifyInboxEntryUserCommunication(entryId, {
                messageId: id,
                labelIds: [...(comm.labelIds || []), LabelId.UNREAD].filter(
                  (label, index, arr) => arr.indexOf(label) === index,
                ),
              });
            });
          });

          unreadUserCommunicationsInInboxEntries(succeededMessageIds);
        }

        if (failed.length === 0) {
          onSuccess?.();
        } else {
          log.warn(
            `${failed.length} operations failed out of ${validCommunications.length}`,
          );

          errorMessage(
            failed.length === validCommunications.length
              ? t('inbox.userCommunication.actions.markAsUnreadError')
              : t('inbox.userCommunication.actions.partialMarkAsUnreadError', {
                  failed: failed.length,
                  total: validCommunications.length,
                }),
          );
        }
      } catch (error) {
        log.error(error);
        errorMessage(t('inbox.userCommunication.actions.markAsUnreadError'));
      } finally {
        setMarkAsUnreadLoading(false);
      }
    },
    [
      errorMessage,
      t,
      user?.id,
      unReadUserCommunication,
      setMarkAsUnreadLoading,
      unreadUserCommunicationsInInboxEntries,
      getUserCommunicationByMessageId,
      modifyInboxEntryUserCommunication,
    ],
  );

  const extractMessageIdFromSelectedItems = useCallback(
    (selectedItems: Set<string>) => {
      return Array.from(selectedItems).map((messageId) => messageId);
    },
    [],
  );

  return {
    handleMarkAsUnreadUserCommunication,
    extractMessageIdFromSelectedItems,
    skipEffectRef,
  };
};
